import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { CREATE_TEST_INSPECTION } from '../graphql/mutations';
import { GET_ALL_PERMITS } from '../graphql/queries';

const CreateInspectionModal = ({ isOpen, onClose }) => {
   const [formData, setFormData] = useState({
      permitId: '',
      scheduledDate: format(new Date(), 'yyyy-MM-dd'),
      scheduledTime: '09:00',
      location: 'DENR Office'
   });

   const { data: permitsData, loading: permitsLoading } = useQuery(GET_ALL_PERMITS);
   const [createInspection, { loading }] = useMutation(CREATE_TEST_INSPECTION);

   const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({
         ...prev,
         [name]: value
      }));
   };

   const handlePermitSelect = (permitId) => {
      setFormData(prev => ({
         ...prev,
         permitId
      }));
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      try {
         const { data } = await createInspection({
            variables: {
               permitId: formData.permitId
            }
         });

         if (data?.createTestInspection) {
            const permit = permitsData?.getAllPermits.find(p => p.id === formData.permitId);
            toast.success('Test inspection scheduled successfully', {
               description: `Created for application: ${permit?.applicationNumber || formData.permitId}`
            });
            onClose();
         }
      } catch (error) {
         toast.error('Failed to schedule inspection', {
            description: error.message
         });
      }
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent>
            <DialogHeader>
               <DialogTitle>Schedule Test Inspection</DialogTitle>
               <DialogDescription>
                  Create a test inspection schedule for any application (Testing Mode)
               </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
               <div className="space-y-2">
                  <Label>Select Application</Label>
                  <Select value={formData.permitId} onValueChange={handlePermitSelect}>
                     <SelectTrigger>
                        <SelectValue placeholder="Select an application" />
                     </SelectTrigger>
                     <SelectContent>
                        {permitsLoading ? (
                           <SelectItem value="loading" disabled>Loading applications...</SelectItem>
                        ) : permitsData?.getAllPermits.map((permit) => (
                           <SelectItem key={permit.id} value={permit.id}>
                              {permit.applicationNumber}  {/* - {permit.applicationType} [{permit.status}] */}
                           </SelectItem>
                        ))}
                     </SelectContent>
                  </Select>
               </div>

               <div className="space-y-2">
                  <Label htmlFor="scheduledDate">Date</Label>
                  <Input
                     id="scheduledDate"
                     name="scheduledDate"
                     type="date"
                     value={formData.scheduledDate}
                     onChange={handleChange}
                     min={format(new Date(), 'yyyy-MM-dd')}
                     required
                  />
               </div>

               <div className="space-y-2">
                  <Label htmlFor="scheduledTime">Time</Label>
                  <Input
                     id="scheduledTime"
                     name="scheduledTime"
                     type="time"
                     value={formData.scheduledTime}
                     onChange={handleChange}
                     min="09:00"
                     max="16:00"
                     required
                  />
                  <p className="text-sm text-muted-foreground">Business hours: 9:00 AM - 4:00 PM</p>
               </div>

               <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                     id="location"
                     name="location"
                     value={formData.location}
                     onChange={handleChange}
                     placeholder="Enter inspection location"
                     required
                  />
               </div>

               <Button type="submit" disabled={loading || !formData.permitId}>
                  {loading ? (
                     <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Scheduling...
                     </>
                  ) : (
                     'Schedule Inspection'
                  )}
               </Button>
            </form>
         </DialogContent>
      </Dialog>
   );
};

export default CreateInspectionModal;